<script setup lang="ts">
import {
    faArrowRightFromBracket,
    faBriefcase,
    faChevronLeft,
    faEnvelope,
    faPlaneDeparture,
    faSuitcaseRolling,
    faTicket,
    faWallet,
    faUserGroup,
    faBell,
    faBolt,
    faCircleInfo,
    faBadgePercent,
} from '@fortawesome/pro-regular-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import SidebarNavItem from './SidebarNavItem.vue';
import NavSubItem from './NavSubItem.vue';
import { phone24 } from '@static/customIcons';
import { UserType } from '@store/auth';
import useBillTracking from '@composable/useBillTracking';

const authStore = useAuthStore();
const { logout } = useAuth();
const { userInfo, userHasUnreadMessage } = classToRefs(authStore);
const mounted = useMounted();
const route = useRoute();
const onTrackingRoute = computed(() => route.path === '/panel/reserves');
const privacy = computed(() => {
    if (mounted.value) {
        if (authStore.loggedIn) {
            return 'private';
        } else {
            return 'public';
        }
    }
    return undefined;
});
const showPrivate = computed(() => privacy.value === 'private');
const userType = computed(() => authStore.currentUserType);
const isExtraFeaturesEnabled = computed(
    () => useLayoutStore().isExtraFeaturesEnabled
);
const onTracking = useBillTracking();
const credit = computed(() => userInfo.value?.currentCredit || 0);
const creditSubtitle = computed(() => {
    const creditText = formatMoney(credit.value || 0);
    if (credit.value > 0) {
        return creditText;
    }
    const days = userInfo.value?.lastStatusReportCheckoutDays || 0;
    return `${creditText}
    از آخرین صورت وضعیت تسویه نشده شما ${days || 0} روز گذشته است.`;
});
const layoutStore = useLayoutStore();
const { organizationalSupportNumber } = classToRefs(layoutStore);
</script>

<template>
    <ul class="menu">
        <SidebarNavItem
            title="پیگیری خرید و کنسلی"
            :icon="faTicket"
            :class="{ onTrackingRoute }"
            @click="onTracking"
        />
        <SidebarNavItem
            v-if="isExtraFeaturesEnabled && showPrivate"
            title="مدیریت رزرو خودکار"
            to="/panel/auto-reserve"
            :icon="{
                icon: faBolt,
                color: 'var(--cool-gray-800)',
            }"
        />
        <SidebarNavItem
            v-if="isExtraFeaturesEnabled && showPrivate"
            title="مدیریت رهگیری"
            to="/panel/trackings"
            :icon="{
                icon: faBell,
                color: 'var(--cool-gray-800)',
            }"
        />
        <SidebarNavItem
            v-if="userType === UserType.User && showPrivate"
            title="اعتبار کاربری"
            to="/panel/credit"
            :icon="{
                icon: faWallet,
                color: 'var(--cool-gray-800)',
            }"
        />
        <SidebarNavItem
            v-if="
                userType === UserType.Organization ||
                userType === UserType.Intermediate
            "
            title="گزارش‌های مالی و سفرها"
            :icon="faBriefcase"
        >
            <NavSubItem
                title="اعتبار کاربری"
                position="sidebar"
                :subtitle="creditSubtitle"
                to="/panel/credit"
                class="credit"
                :append-icon="{
                    icon: faChevronLeft,
                    color: 'var(--yellow-700)',
                }"
            />
            <NavSubItem
                title="گزارش خریدها"
                position="sidebar"
                to="/org/purchaseReport"
            />
            <NavSubItem
                title="دریافت صورت‌وضعیت"
                position="sidebar"
                to="/org/statusReport"
            />
            <NavSubItem
                v-if="userInfo?.canDownloadHotelFactor !== false"
                title="دریافت فاکتور هتل"
                position="sidebar"
                to="/org/hotelFactor"
            />
            <NavSubItem
                title="دریافت کلی فایل‌های بلیط"
                position="sidebar"
                to="/org/downloadTickets"
            />
        </SidebarNavItem>
        <SidebarNavItem title="پشتیبانی ۲۴/۷" :icon="phone24">
            <NavSubItem
                v-if="userType === UserType.Organization"
                title="تماس با پشتیبانی"
                position="sidebar"
                :href="`tel:+9821${organizationalSupportNumber}`"
            />
            <NavSubItem
                title="پرسش‌های متداول"
                position="sidebar"
                to="/support/faq"
            />
            <NavSubItem
                title="پیشنهادها و شکایات"
                position="sidebar"
                to="/support/feedback"
            />
            <NavSubItem
                title="شرایط و مقررات"
                position="sidebar"
                to="/support/terms-and-conditions"
            />
        </SidebarNavItem>
        <SidebarNavItem
            v-if="isExtraFeaturesEnabled"
            title="خدمات سفر"
            :icon="faSuitcaseRolling"
        >
            <NavSubItem
                title="بلیط هواپیما"
                position="sidebar"
                to="/plane-ticket"
            />
            <NavSubItem title="رزرو هتل" position="sidebar" to="/hotel" />
            <!-- <NavSubItem
                title="بسته سفر"
                position="sidebar"
                to="/tour"
                class="new"
                subtitle="جدید"
            /> -->
            <NavSubItem
                title="بلیط قطار"
                position="sidebar"
                to="/train-ticket"
            />
            <NavSubItem
                title="بلیط اتوبوس"
                position="sidebar"
                to="/bus-ticket"
            />
            <NavSubItem title="بلیط سواری" position="sidebar" to="/taxi" />
        </SidebarNavItem>
        <SidebarNavItem
            v-if="isExtraFeaturesEnabled && showPrivate"
            title="مدیریت مسافران"
            to="/panel/pax"
            :icon="{
                icon: faUserGroup,
                color: 'var(--cool-gray-800)',
            }"
        />
        <SidebarNavItem
            title="اطلاعات پرواز"
            to="/flight-info"
            :icon="faPlaneDeparture"
        />
        <SidebarNavItem
            v-if="showPrivate"
            title="ربات مِستربلیط"
            to="/panel/robot"
            :icon="faTelegram"
        />
        <SidebarNavItem
            v-if="showPrivate"
            title="سفر کارت‌ها"
            to="/panel/gift-cards"
            :icon="{
                icon: faBadgePercent,
                color: 'var(--cool-gray-800)',
            }"
        />
        <SidebarNavItem
            v-if="showPrivate"
            title="صندوق پیام"
            to="/panel/messages"
            :icon="faEnvelope"
            :class="{ unread: userHasUnreadMessage }"
        />
        <SidebarNavItem
            title="درباره‌ مِستربلیط"
            to="/support/who-is-he"
            :icon="faCircleInfo"
        />
        <!-- <SidebarNavItem
            v-if="
                isMrbilit &&
                (userType === UserType.User || privacy === 'public')
            "
            title="راهکارهای سازمانی"
            to="/b2b"
            :icon="faUserTie"
        /> -->
        <SidebarNavItem
            v-if="showPrivate"
            title="خروج"
            :icon="faArrowRightFromBracket"
            class="logout"
            @click="logout"
        />
    </ul>
</template>

<style scoped lang="scss">
.onTrackingRoute {
    :deep(.body) {
        .title {
            color: usePalette($p-blue) !important;
        }
        .mr-icon-svg {
            color: usePalette($p-blue) !important;
        }
    }
}
.menu {
    list-style: none none;
    margin: 0;
    background: white;
    width: 100%;
    user-select: none;
    padding: 0 20px 6px !important;
}
.logout {
    :deep(.title),
    :deep(.mr-icon-svg) {
        color: usePalette($p-red) !important;
    }
}
.new {
    :deep(.sub-body) {
        display: flex;
        align-items: center;
        .main {
            width: auto;
            padding-left: 4px;
        }
        .sub-subtitle {
            padding: 2px 4px 4px;
            background-color: usePalette($p-yellow, $p-200);
            border: 1px solid usePalette($p-yellow, $p-300);
            border-radius: 4px;
            @include tg-xs($color: usePalette($p-yellow, $p-700));
            line-height: 10px; // @warn uncontrolled property modify
        }
    }
}
.credit {
    margin: 10px 14px 0 14px !important;
    border-radius: 8px;
    background-color: usePalette($p-yellow, $p-200) !important;
    padding: 8px !important;
    :deep(.sub-body) {
        .main {
            padding: 0 !important;
        }
        .sub-title {
            @include tg-m($w7, usePalette($p-yellow, $p-700), true);
            flex-grow: 1;
        }
        .sub-subtitle {
            @include tg-s($color: usePalette($p-yellow, $p-700));
            white-space: pre-line;
        }
    }
}
</style>
